import { useMemo, useState } from "react";
import QuestionnaireStepView from "./QuestionnaireStepView";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import useCustomEffect from "../../../../hooks/useCustomEffect";

function QuestionnaireStep({ controller }) {
  const callEvaApi = useEvaApi();
  const answeredQuestions = getQuestionsAnswered();
  const fetchedQuestionnaireData =
    controller.visitorData[
      `fetched_questionnaire_${controller.step.questionnaire?.id}`
    ];
  const [forceAnswer, setForceAnswer] = useState(false);

  //Determine which questions to show
  const questions = useMemo(() => {
    //Include all questions that are not in a group
    let newQuestions = controller.step.questionnaire?.questions.filter(
      (question) => !question.group,
    );

    //Group questions by group
    const groups = controller.step.questionnaire?.questions.reduce(
      (acc, question) => {
        if (!question.group) {
          return acc;
        }
        acc[question.group] = !acc[question.group]
          ? [question]
          : [...acc[question.group], question];
        return acc;
      },
      {},
    );

    //For each group, pick a random question to include
    Object.keys(groups || {}).forEach((group) => {
      const randomIndex = Math.floor(Math.random() * groups[group].length);
      newQuestions = [
        ...newQuestions,
        groups[group].find(
          (question) =>
            controller.visitorData[`question_${question.id}`] !== undefined,
        ) ?? groups[group][randomIndex],
      ];
    });

    return newQuestions;
  }, [controller.step.questionnaire]); // eslint-disable-line react-hooks/exhaustive-deps

  // UseEffect for fetching if the questionnaire was accepted
  useCustomEffect(() => {
    fetchAccepted();
  });

  /**
   * This method will be called when a question answer is changed
   * @param {object} question
   * @param {int} answer
   */
  function onQuestionAnswerChange(question, answer) {
    controller.setVisitorData((prev) => ({
      ...prev,
      [`question_${question.id}`]: answer,
    }));
  }

  /**
   * Check which questions have been answered already by the visitor
   * @returns {array}
   */
  function getQuestionsAnswered() {
    return Object.keys(controller.visitorData).filter(
      (key) =>
        key.startsWith("question_") &&
        controller.step.questionnaire?.questions.find(
          (q) => `question_${q.id}` === key,
        ),
    );
  }

  /**
   * This method will fetch if the questionnaire was accepted
   */
  async function fetchAccepted() {
    await callEvaApi(
      new EvaApiCall("visitors/accepted/questionnaire")
        .setLoadingGroup("flow_player_next_step")
        .setParams({
          ...controller.visitorData,
          flow_step_id: controller.step.id,
        })
        .setAlertError(false)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          const accepted = response.data.data.accepted;
          const requiresAnswers =
            controller.step.link_to_houserules &&
            Object.keys(controller.visitorData).some((key) =>
              key.startsWith("houserules_"),
            );

          if (!accepted || requiresAnswers) {
            onFetchedQuestionnaireChange(undefined);
          } else {
            onFetchedQuestionnaireChange(true);
          }
        }),
    );
  }

  /**
   * This method will be called when the fetched questionnaire data changes
   * @param {any} value
   */
  function onFetchedQuestionnaireChange(value) {
    controller.setVisitorData((prevData) => ({
      ...prevData,
      [`fetched_questionnaire_${controller.step.questionnaire?.id}`]: value,
    }));
  }

  return (
    <QuestionnaireStepView
      controller={controller}
      questions={questions}
      fetchedQuestionnaireData={fetchedQuestionnaireData}
      answeredQuestions={answeredQuestions}
      forceAnswer={forceAnswer}
      setForceAnswer={setForceAnswer}
      onQuestionAnswerChange={onQuestionAnswerChange}
    />
  );
}

export default QuestionnaireStep;
